import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

interface AccountLoadFailureScreenProps {
  retrying: boolean;
  onRetry: () => void;
  onSignOut: () => void;
}

/**
 * Shown in place of the whole portal when a signed-in user's HopDrive account (their profile)
 * did not load. Without the profile the portal has no rooftop, no permissions and no
 * products, so it renders nothing else: no sidebar, no "Unknown User", no "no products"
 * welcome. Retry runs the account load again on the same Firebase session; Sign out ends it.
 */
const AccountLoadFailureScreen: React.FC<AccountLoadFailureScreenProps> = ({ retrying, onRetry, onSignOut }) => (
  <Box
    role='alert'
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      px: 3,
      textAlign: 'center',
    }}
  >
    <Typography variant='h5' component='h1'>
      We couldn't load your account
    </Typography>
    <Typography color='text.secondary' sx={{ maxWidth: 480 }}>
      You're signed in, but we couldn't load your HopDrive account details. Check your connection and try again. If this
      keeps happening, contact HopDrive support.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
      <Button variant='contained' onClick={onRetry} disabled={retrying} startIcon={retrying ? <CircularProgress size={16} /> : undefined}>
        {retrying ? 'Retrying' : 'Retry'}
      </Button>
      <Button variant='outlined' onClick={onSignOut} disabled={retrying}>
        Sign out
      </Button>
    </Box>
  </Box>
);

export default AccountLoadFailureScreen;
